import { Card, useTheme, Grid, IconButton, Divider, LinearProgress, Tooltip, Typography, Box, LinearProgressProps, Badge, styled, TextField } from "@mui/material";
import { calendarEntriesApi, shiftApi } from "api";
import FlexBox from "components/flexbox/FlexBox";
import { FC, useEffect, useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import { Position } from "pages/shifts/shift-assignment";
import { useSeason } from "contexts/SeasonContext";
import AppAvatar from "components/avatars/AppAvatar";
import defaultProfileImage from '../../assets/images/default-profile.png';
import { useNavigate } from "react-router-dom";
import { CalendarEntryEntity } from "api/generated";
import { format, isSameDay } from "date-fns";
import { calendarEntryColor } from "utils/utils";

export interface EmployeeCardData {
    id: number;
    name: string;
    propicPath: string | null;
    workPosition: string;
    workedHours: number;
    contractHours: number;
    hasBeenAsked?: boolean;
    hasResponded?: boolean;
    hasConfirmed?: boolean;
}

const StyledBadge = styled(Badge)<{ bgColor: string }>(({ theme, bgColor }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: bgColor,
        color: '#ef9514',
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        '&::after': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            animation: 'ripple 1.2s infinite ease-in-out',
            border: '1px solid currentColor',
            content: '""',
        },
    },
    '@keyframes ripple': {
        '0%': {
            transform: 'scale(.8)',
            opacity: 1,
        },
        '100%': {
            transform: 'scale(2.4)',
            opacity: 0,
        },
    },
}));

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">
                    {`${Math.round(props.value)}%`}
                </Typography>
            </Box>
        </Box>
    );
}

interface DataPropos {
    data: EmployeeCardData[];
    selectedShiftDetail?: Position;
    fetchData: () => void;
    departmentId: number;
}

const EmployeeCards: FC<DataPropos> = (props) => {
    const { data, selectedShiftDetail, fetchData } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { seasonId } = useSeason();
    const navigate = useNavigate();
    const [employeesStatus, setEmployeesStatus] = useState<CalendarEntryEntity[]>([]);

    // NEW: Modern search input for filtering employees
    const [searchTerm, setSearchTerm] = useState("");

    // Minimal function to decide sorting priority
    const getSortRank = (item: EmployeeCardData) => {
        if (!item.hasBeenAsked) return 999; // keep non-asked employees last
        if (item.hasResponded && item.hasConfirmed) return 0; // responded & confirmed
        if (!item.hasResponded) return 1;                    // no response
        return 2;                                            // responded but not confirmed
    };

    useEffect(() => {
        if (selectedShiftDetail && data) {
            calendarEntriesApi.findByDate(
                seasonId,
                selectedShiftDetail.date.toISOString(),
                props.departmentId
            ).then(({ data }) => {
                setEmployeesStatus(data);
            });
        }
    }, [data, selectedShiftDetail]);

    // We keep your existing logic for the “divider” entirely unmodified:
    const firtstPosition = data.at(0)?.workPosition;
    const dividerPosition = data.findIndex((value) => value.workPosition !== firtstPosition);

    // 1) Filter by searchTerm
    let processedData = data.filter(item =>
        !searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 2) Sort in subgroups so we preserve your grouping logic
    if (dividerPosition !== -1) {
        const group1 = processedData.slice(0, dividerPosition).sort((a, b) => getSortRank(a) - getSortRank(b));
        const group2 = processedData.slice(dividerPosition).sort((a, b) => getSortRank(a) - getSortRank(b));
        processedData = [...group1, ...group2];
    } else {
        processedData = processedData.sort((a, b) => getSortRank(a) - getSortRank(b));
    }

    const assignEmployee = async (employeeId: number) => {
        if (!selectedShiftDetail) return;
        await shiftApi.assignEmployee({
            date: selectedShiftDetail.date.toISOString(),
            employeeId,
            seasonId,
            shiftDetailId: selectedShiftDetail.id
        });
        fetchData();
    };

    const navigateToEmployee = (employeeId: number) => {
        navigate('/dashboard/employees/new', {
            state: {
                employeeId,
                activeTab: 0,
            },
        });
    };

    const renderAvailabilityIndicator = (card: EmployeeCardData) => {
        if (!card.hasBeenAsked) return null;
        let dotColor = "#9c9c9c";
        let label = t("shift.noResponse");

        if (card.hasResponded && card.hasConfirmed) {
            dotColor = "#027d1f";
            label = t("shift.confirmed");
        } else if (card.hasResponded && !card.hasConfirmed) {
            dotColor = "#ed1202";
            label = t("shift.notConfirmed");
        }

        return (
            <Box display="flex" alignItems="center" mt={0.5}>
                <Box
                    width={8}
                    height={8}
                    borderRadius="50%"
                    mr={1}
                    sx={{ backgroundColor: dotColor }}
                />
                <Typography variant="caption">{label}</Typography>
            </Box>
        );
    };

    return (
        <FlexBox flexDirection={'column'} borderRight={1} borderColor={theme.palette.divider} height={'100%'}>
            {/* NEW: Small, sleek “search box” */}
            <Box m={2}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder={t("employees.searchEmployees")}
                    value = { searchTerm }
                    onChange = {(e) => {setSearchTerm(e.target.value)}}
                />
            </Box>

            {
                processedData.map((card, index) => {
                    const employeeEvent = employeesStatus.find((ev) => ev.employeeId === card.id);

                    return (
                        <div key={card.id}>
                            {
                                dividerPosition === index && selectedShiftDetail && (
                                    <Divider>
                                        {t('shift.otherEmployees')}
                                    </Divider>
                                )
                            }
                            <Card
                                sx={{
                                    borderRadius: 2,
                                    display: 'block',
                                    padding: 1,
                                    margin: 2,
                                    marginY: 1,
                                    bgcolor: theme.palette.background.default
                                }}
                            >
                                <Grid container alignItems={'center'}>
                                    <Grid item xs={3} justifyContent={'center'} display={'flex'}>
                                        {employeeEvent ? (
                                            <Tooltip
                                                title={
                                                    t(`employees.calendar.types.${employeeEvent.type.toLowerCase()}`) +
                                                    ': ' +
                                                    (isSameDay(new Date(employeeEvent.startedOn), new Date(employeeEvent.endedOn))
                                                        ? format(new Date(employeeEvent.startedOn), "dd/MM/yyyy")
                                                        : format(new Date(employeeEvent.startedOn), "dd/MM/yyyy") + "-" + format(new Date(employeeEvent.endedOn), "dd/MM/yyyy")
                                                    )
                                                }
                                            >
                                                <StyledBadge
                                                    overlap="circular"
                                                    variant="dot"
                                                    bgColor={calendarEntryColor(employeeEvent.type.toUpperCase() as any, theme)}
                                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                >
                                                    <AppAvatar
                                                        sx={{ width: "2.0vw", height: "2.0vw", display: 'flex' }}
                                                        src={card.propicPath ?? defaultProfileImage}
                                                    />
                                                </StyledBadge>
                                            </Tooltip>
                                        ) : (
                                            <AppAvatar
                                                sx={{ width: "2.0vw", height: "2.0vw", display: 'flex' }}
                                                src={card.propicPath ?? defaultProfileImage}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={5} md={7} zeroMinWidth>
                                        <Typography
                                            fontWeight={600}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: "underline #000000" }
                                            }}
                                            overflow={'hidden'}
                                            textOverflow={'ellipsis'}
                                            onClick={() => { navigateToEmployee(card.id) }}
                                        >
                                            {card.name}
                                        </Typography>

                                        <Typography
                                            fontStyle={'italic'}
                                            fontWeight={50}
                                            overflow={'hidden'}
                                            textOverflow={'ellipsis'}
                                        >
                                            {card.workPosition}
                                        </Typography>

                                        <Tooltip title={t('shift.workedHours') + ' ' + card.workedHours + '/' + card.contractHours}>
                                            <div>
                                                <LinearProgressWithLabel
                                                    variant={'determinate'}
                                                    value={card.workedHours / card.contractHours * 100}
                                                />
                                            </div>
                                        </Tooltip>

                                        {renderAvailabilityIndicator(card)}
                                    </Grid>
                                    <Grid item xs={4} md={2}>
                                        {
                                            selectedShiftDetail && (
                                                <IconButton
                                                    size="medium"
                                                    color="primary"
                                                    onClick={() => { assignEmployee(card.id) }}
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            )
                                        }
                                    </Grid>
                                </Grid>
                            </Card>
                        </div>
                    );
                })
            }
        </FlexBox>
    );
};

export default EmployeeCards;
